import type { ReactNode } from "react";
import type { FieldPrimitiveProps } from "./types";

// Shared rendering for stub primitives. Per CHG-1167 every primitive ships
// as a stub with this rendering — real implementations land per-primitive
// in the ε CHGs listed in specs/_product/field-primitive-registry.md §1.
//
// data-field-stub="true" is the lint marker that flags scaffold-era stubs
// reaching production (lint sweep lands α CHG-1133).

export function FieldStub<T>({
  fieldType,
  inputType,
  hint,
  ...props
}: FieldPrimitiveProps<T> & {
  fieldType: string;
  inputType?: string;
  hint?: ReactNode;
}) {
  const { name, value, onChange, label, error, disabled, required, helperText } = props;
  // Stub renders all values as strings; real primitives (ε CHGs) parse properly.
  const stringValue = value == null ? "" : typeof value === "string" ? value : String(value);
  return (
    <label
      data-field-stub="true"
      data-field-type={fieldType}
      style={{ display: "flex", flexDirection: "column", gap: 4 }}
    >
      {label ? (
        <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          {label} {required ? "*" : null}
        </span>
      ) : null}
      <input
        name={name}
        type={inputType ?? "text"}
        disabled={disabled}
        required={required}
        value={stringValue}
        onChange={(e) => onChange(e.target.value === "" ? null : (e.target.value as unknown as T))}
        className="input"
        style={{
          height: 36,
          padding: "0 12px",
          borderRadius: "var(--radius-md)",
          border: error ? "1px solid var(--color-error)" : "1px solid var(--color-border)",
          background: "var(--color-surface)",
          fontSize: 13,
          color: "var(--color-text)",
        }}
      />
      {hint ? <small style={{ color: "var(--color-text-muted)" }}>{hint}</small> : null}
      {helperText ? <small style={{ color: "var(--color-text-muted)" }}>{helperText}</small> : null}
      {error ? <small style={{ color: "var(--color-error)" }}>{error}</small> : null}
    </label>
  );
}
